import React, { createContext, useContext, useEffect } from "react";
import {
  getCollectorBoosters,
  getCollectorsArchivedBoosters,
} from "../../services/CollectorServices";
import { boosterData } from "../../services/BoosterServices";
import { useAuth } from "../AuthProvider";
import {
  usePaginatedData,
  UsePaginatedDataResult,
} from "../helper/usePaginatedData";

interface BoosterContextProps {
  boostersState: UsePaginatedDataResult<boosterData>;
  archivedBoostersState: UsePaginatedDataResult<boosterData>;
}

const BoosterContext = createContext<BoosterContextProps | undefined>(
  undefined
);

export const CollectorProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const boostersState = usePaginatedData<boosterData>();
  const archivedBoostersState = usePaginatedData<boosterData>();
  const { user } = useAuth();
  const limit = 8;

  useEffect(() => {
    const fetchBoosters = async () => {
      if (boostersState.loading || !boostersState.hasMore) return;

      boostersState.setLoading(true);
      boostersState.setError(null);
      try {
        const offset = (boostersState.page - 1) * limit;
        const fetchedBoosters = await getCollectorBoosters(limit, offset);

        if (fetchedBoosters.length < limit) {
          boostersState.setHasMore(false);
        }

        boostersState.setData((prevBoosters) => {
          const newBoosters = fetchedBoosters.filter(
            (fetchedBooster) =>
              !prevBoosters.some(
                (prevBooster) => prevBooster.id === fetchedBooster.id
              )
          );
          return [...prevBoosters, ...newBoosters];
        });

        boostersState.setFilteredData((prevBoosters) => {
          const newBoosters = fetchedBoosters.filter(
            (fetchedBooster) =>
              !prevBoosters.some(
                (prevBooster) => prevBooster.id === fetchedBooster.id
              )
          );
          return [...prevBoosters, ...newBoosters];
        });
      } catch (err) {
        boostersState.setError("Failed to fetch boosters");
      } finally {
        boostersState.setLoading(false);
      }
    };

    if (user && boostersState.page > 0) fetchBoosters();

    // only rerun on page change ...
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boostersState.page]);

  useEffect(() => {
    if (user && archivedBoostersState.page > 0)
      archivedBoostersState.fetchData(limit, getCollectorsArchivedBoosters);

    // only rerun on page change ...
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archivedBoostersState.page]);

  useEffect(() => {
    boostersState.setPage(0);
    boostersState.setHasMore(true);
    boostersState.setData([]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <BoosterContext.Provider
      value={{
        boostersState,
        archivedBoostersState,
      }}
    >
      {children}
    </BoosterContext.Provider>
  );
};

export const useCollectorBoostersContext = () => {
  const context = useContext(BoosterContext);
  if (!context) {
    throw new Error("useBoosterContext must be used within a BoosterProvider");
  }
  return context;
};
